import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  message,
  Row,
  Spin,
} from 'antd';
import { getMetodosDePago } from 'api/configuracion';
import { getBancos } from 'api/configuracion/bancos';
import { venta } from 'api/netpay';
import NumericInput from 'components/NumericInput';
import Select from 'components/Select';
import useCorte from 'hooks/useCorte';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SET_LAST_PAYMENT_DATA_CONVENIO } from 'store/reducers/caja';
import getFormattedValues, { formatReceived } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import { decimalNormalizer, INTEGER_PATTERN_REPLACE } from 'utils/normalizers';
import round from 'utils/round';
import currencyFormatter from 'utils/toCurrency';

/**
 * Convierte un importe a centavos
 * @param {number} amount - El importe a convertir
 * @returns {number} - El importe en centavos
 */
export const toCents = (amount) => Math.round(amount * 100);

/**
 * Convierte un importe en centavos a importe
 * @param {number} cents - El importe en centavos
 * @returns {number} - El importe
 */
export const fromCents = (cents) => cents / 100;

// Constantes para métodos de pago
const PAYMENT_METHODS = {
  EFECTIVO: 1,
  T_CREDITO: 4,
  T_DEBITO: 18,
  NETPAY: 24,
  DEPOSITO: 25,
  CHEQUE: 2,
  TRANSFERENCIA: 3,
  DEPOSITO_CUENTA: 19,
};

// Configuraciones para campos dinámicos
const FIELD_CONFIGS = {
  METHODS_REQUIRING_ACCOUNT: [4, 18, 28, 19, 5, 3, 2, 6],
  METHODS_REQUIRING_DATE: [2, 3, 6, 12, 19],
  METHODS_REQUIRING_SUCURSAL: [3, 2],
  ACCOUNT_LABELS: {
    19: 'Cuenta de depósito',
    default: 'Últimos 4 digitos de la Cuenta/Tarjeta',
  },
  DATE_LABELS: {
    2: 'Fecha de Remisión del Cheque',
    default: 'Fecha de Autorización',
  },
};

// Funciones utilitarias
const calculateTotalPayments = (payments) => {
  const totalInCents = payments.reduce(
    (total, payment) => total + toCents(parseFloat(payment.importe || 0)),
    0,
  );
  return fromCents(totalInCents);
};

const calculateChange = (payments, currentImport, total) => {
  const currentTotalCents = toCents(calculateTotalPayments(payments));
  const currentImportCents = toCents(parseFloat(currentImport || 0));
  const totalCents = toCents(total);
  return fromCents(currentTotalCents + currentImportCents - totalCents);
};

const validatePayment = (payments, change) => {
  const cashPayment = payments.find((p) => p.metodo === PAYMENT_METHODS.EFECTIVO);

  if (!change) return true;

  // Convertir a centavos para comparar
  const changeCents = toCents(change);
  const cashImporteCents = cashPayment ? toCents(cashPayment.importe) : 0;

  if (cashPayment && changeCents > cashImporteCents) {
    message.warn('El cambio no puede ser mayor al importe del efectivo');
    return false;
  }

  if (!cashPayment) {
    message.warn('No puede haber cambio con los métodos de pago seleccionados');
    return false;
  }

  return true;
};

const getFieldLabel = (methodId, labelType) => {
  const labels = FIELD_CONFIGS[labelType];
  return labels[methodId] || labels.default;
};

const shouldShowField = (methodId, fieldType) => {
  const methods = FIELD_CONFIGS[fieldType];
  return methods.includes(methodId);
};

const FormDatosDePago = ({
  data,
  setData,
  onPayment,
  total = 0,
  setNetPayLoading,
  callback,
  autoFillImporte,
  showImports,
  allowAnyImporte,
  setVisibleGetRecibos,
  isConvenio = false,
  onNetPayConvenio,
  padron: padronConvenio,
  parcialidades,
  convenio,
}) => {
  const dispatch = useDispatch();
  const { entidad: _entidad, user } = useSelector(({ auth }) => auth);
  const { solo_pago_netpay, metodo_de_pago_principal } = _entidad;
  const { corte, alerta } = useCorte();
  const { ciudadano } = useSelector(({ caja }) => caja);

  const metodoDefault = solo_pago_netpay ? PAYMENT_METHODS.NETPAY : metodo_de_pago_principal;
  const [form] = Form.useForm();

  const [selectedMetodo, setSelectedMetodo] = useState(metodoDefault);
  const [cambio, setCambio] = useState(0);
  const [editedPago, setEditedPago] = useState();
  const [metodosDePago, setMetodosDePago] = useState([]);
  const [bancos, setBancos] = useState([]);
  const netpayRef = useRef();
  const [loading, setLoading] = useState(true);

  const _metodo = (selectedMetodo || Form.useWatch('metodo', form));
  const requiereDatosBancarios = metodosDePago
    // eslint-disable-next-line max-len
    .find((e) => e.id === _metodo)?.requiere_datos_bancarios;

  const inputImporteRef = useRef();
  const dataCopy = useRef([]);

  const importField = Form.useWatch('importe', form);

  const focusInputImporte = useCallback(() => setTimeout(() => inputImporteRef
    .current?.focus()), []);

  const getTotalMetodos = useCallback((_dataPagos = data) => calculateTotalPayments(_dataPagos),
    [data]);

  useEffect(() => {
    if (autoFillImporte) {
      setTimeout(() => {
        form.setFieldsValue({ importe: total });
      });
    }
  }, [total, autoFillImporte, form]);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      const [_metodos, _bancos] = await Promise.all([
        getMetodosDePago(),
        getBancos(),
      ]);
      if (mounted) {
        setMetodosDePago(_metodos);
        setBancos(_bancos);
        setLoading(false);
        setData([]);
      }
    };
    fetchData();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const _metodos = metodosDePago.map((item) => ({
      ...item,
      disabled: data.some((d) => d.metodo === PAYMENT_METHODS.EFECTIVO)
        && item.id === PAYMENT_METHODS.EFECTIVO,
      prioridad: item.id === PAYMENT_METHODS.EFECTIVO ? -1 : 4,
    }));
    setMetodosDePago(_metodos.sort((a, b) => a.prioridad - b.prioridad));
  }, [data]);

  const getFaltante = useCallback((_dataPagos = data) => {
    const totalCents = toCents(total);
    const paidCents = toCents(getTotalMetodos(_dataPagos));
    return fromCents(Math.max(totalCents - paidCents, 0));
  }, [data, total, getTotalMetodos]);

  const onChangeImporte = useCallback((e) => {
    if (callback) callback(e);
    const { selectionStart, value } = e.target;
    const _importe = parseFloat(value || 0);

    // Calcular cambio usando la función utilitaria
    const currentPayments = data.filter((d) => d.key !== editedPago?.key);
    const newChange = calculateChange(currentPayments, _importe, total);
    // Redondear a 2 decimales para evitar problemas de precisión en comparaciones
    setCambio(Math.round(newChange * 100) / 100);

    focusInputImporte();
    setTimeout(() => {
      const len = selectionStart?.length || selectionStart;
      const position = Math.max(selectionStart > len ? len : selectionStart, 1);
      inputImporteRef.current?.input?.setSelectionRange(position, position);
    });
  }, [callback, data, editedPago, total, focusInputImporte]);

  useEffect(() => {
    dataCopy.current = [...data];
  }, [data]);

  const isValid = useCallback((_dataPagos) => {
    if (!showImports) return true;
    const _cambio = typeof cambio === 'string' ? parseFloat(cambio) : cambio;
    return validatePayment(_dataPagos, _cambio);
  }, [showImports, cambio]);

  const agregarDatoDePago = async (datoDePago = null, _handlePay = false) => {
    try {
      const values = getFormattedValues({ ...form.getFieldsValue(), ...datoDePago });
      const copyDataPagos = [...data];

      // Solo agregar si no es para pagar directamente
      if (!_handlePay) {
        const newPayment = {
          ...editedPago,
          ...values,
          importe: parseFloat(values.importe),
          key: editedPago?.key || Date.now(),
        };
        copyDataPagos.push(newPayment);
        setData(copyDataPagos.map((e, idx) => ({ ...e, key: idx })));

        // Resetear formulario para siguiente pago
        setEditedPago();
        let faltante = getFaltante(copyDataPagos);

        // Si el método de pago agregado es EFECTIVO y hay cambio, restar del faltante
        if (values.metodo === PAYMENT_METHODS.EFECTIVO && cambio > 0) {
          faltante = Math.max(0, faltante - cambio);
        }

        const metodo = !copyDataPagos.some((item) => item.metodo === PAYMENT_METHODS.EFECTIVO)
          ? PAYMENT_METHODS.EFECTIVO
          : PAYMENT_METHODS.T_DEBITO;
        form.setFieldsValue({
          metodo,
          importe: faltante,
        });
        setSelectedMetodo(metodo);
        setCambio(0);
      }
      if (_handlePay) {
      // eslint-disable-next-line no-use-before-define
        setTimeout(() => handlePay([...copyDataPagos, values]));
      }
    } catch (err) {
      onError(err);
    }
  };

  const localPayment = async (_dataPagos) => {
    if (isValid(_dataPagos)) {
      const output = _dataPagos.map(({
        key, ...rest
      }) => ({
        ...rest,
        importe: rest.metodo === PAYMENT_METHODS.EFECTIVO
          ? rest.importe - cambio : rest.importe,
      }));
      await onPayment(output);
    }
  };

  const buildTraceability = useCallback(() => ({
    padrones: [{
      cargos: padronConvenio.cargos.map(({ id }) => id).join(),
      padron_id: padronConvenio.id,
      tipo_de_padron: padronConvenio.tipo_de_padron,
      parcialidades: parcialidades.join(),
      importe: round(total),
      importe_sin_redondeo: total,
      convenio,
    }],
    es_convenio: true,
    usuario: user.id,
    convenio,
    parcialidades: parcialidades.join(),
    ciudadano: ciudadano.id,
    importe: round(total),
  }), [padronConvenio, parcialidades, total, user, convenio, ciudadano]);

  const payWithNetPay = async () => {
    setNetPayLoading(true);
    const traceability = buildTraceability();
    dispatch({
      type: SET_LAST_PAYMENT_DATA_CONVENIO,
      payload: { parcialidades },
    });

    const response = await venta({
      serialNumber: corte?.terminal_de_netpay?.serial,
      folioNumber: new Date().toISOString(),
      amount: total,
      traceability,
    });
    if (response) {
      netpayRef.current = setTimeout(() => setVisibleGetRecibos(true), 60000);
    } else {
      setNetPayLoading(false);
    }
  };

  const handlePay = async (_dataPagos) => {
    if (selectedMetodo === PAYMENT_METHODS.NETPAY) {
      if (isConvenio && onNetPayConvenio) {
        return onNetPayConvenio();
      }
      return payWithNetPay();
    }
    return localPayment([..._dataPagos]);
  };

  const requireDatosBancarios = useCallback(async (_handlePay = false) => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();

      // Formatear fecha si existe
      if (values.fecha_de_autorizacion) {
        values.fecha_de_autorizacion = values.fecha_de_autorizacion.format('YYYY-MM-DD');
      }

      if (_handlePay) {
        // Si es para pagar directamente, procesar el pago
        agregarDatoDePago(values, true);
      } else {
        // Si es para agregar, guardar en el estado data
        const newPayment = {
          ...values,
          importe: parseFloat(values.importe || 0),
          key: Date.now(), // Generar key único
        };
        setData([...data, newPayment]);

        // Limpiar formulario y resetear para siguiente pago
        form.resetFields();
        let faltante = getFaltante([...data, newPayment]);

        // Si el método de pago agregado es EFECTIVO y hay cambio, restar del faltante
        if (values.metodo === PAYMENT_METHODS.EFECTIVO && cambio > 0) {
          faltante = Math.max(0, faltante - cambio);
        }

        const metodo = ![...data, newPayment]
          .some((item) => item.metodo === PAYMENT_METHODS.EFECTIVO)
          ? PAYMENT_METHODS.EFECTIVO
          : PAYMENT_METHODS.T_DEBITO;
        form.setFieldsValue({
          metodo,
          importe: faltante,
        });
        setSelectedMetodo(metodo);
        setCambio(0);
      }
    } catch (err) {
      onError(err);
    }
  }, [form, data, setData, getFaltante, cambio]);

  const rules = { required: [{ required: true }] };
  const showInput = shouldShowField(selectedMetodo, 'METHODS_REQUIRING_ACCOUNT');
  const label = getFieldLabel(selectedMetodo, 'ACCOUNT_LABELS');

  return (
    <Spin spinning={loading} tip="Cargando...">
      <Row>
        <Col span={24}>
          <Form
            key={selectedMetodo}
            layout="vertical"
            form={form}
            onFinish={cambio >= 0 ? null : () => requireDatosBancarios()}
            initialValues={{ metodo: metodoDefault }}
            style={{ textAlign: 'center' }}
          >
            <Button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!solo_pago_netpay && selectedMetodo !== PAYMENT_METHODS.NETPAY) {
                  const copydataPagos = [...data, form.getFieldsValue()];
                  const willBeValid = isValid(copydataPagos);
                  if (willBeValid) {
                    requireDatosBancarios(true);
                  }
                } else {
                  payWithNetPay();
                }
              }}
              disabled={!allowAnyImporte ? (!corte || cambio < 0 || !importField) : form.getFieldValue('importe') === 0}
              style={{ margin: 0, width: '50%' }}
              id="pay-button"
            >
              <Row justify="center">
                <Col>
                  Pagar
                </Col>
                {!corte && (
                <Col span={24}>
                  {alerta}
                </Col>
                )}
                <Col style={{ marginLeft: 6 }}>
                  {showImports && currencyFormatter(total)}
                </Col>
                {(!solo_pago_netpay && showImports
                  && selectedMetodo !== PAYMENT_METHODS.NETPAY
                  && selectedMetodo === PAYMENT_METHODS.EFECTIVO) && (
                  <Col span={24}>
                    {cambio < 0
                      ? `Falta ${currencyFormatter(-cambio)}`
                      : `Cambio ${currencyFormatter(cambio)}`}
                  </Col>
                )}
              </Row>
            </Button>
            <Row justify="end">
              <Button
                type="link"
                disabled={selectedMetodo === PAYMENT_METHODS.NETPAY
                  || cambio >= 0
                  || form.getFieldValue('importe') === 0}
                onClick={() => {
                  if (!solo_pago_netpay && selectedMetodo !== PAYMENT_METHODS.NETPAY) {
                    const copydataPagos = [...data, form.getFieldsValue()];
                    const willBeValid = isValid(copydataPagos);
                    if (willBeValid) {
                      requireDatosBancarios(false); // false = agregar, true = pagar
                    }
                  }
                }}
                icon={solo_pago_netpay && data[0] ? <EditOutlined /> : <PlusOutlined />}
              >
                {solo_pago_netpay && data[0] ? 'Editar' : 'Agregar'}
              </Button>
            </Row>
            <Col span={24}>
              <Form.Item
                name="metodo"
                label="Método"
              >
                <Select
                  disabled={!corte}
                  dataSource={metodosDePago}
                  onChange={(metodo) => {
                    setSelectedMetodo(metodo);
                    if (solo_pago_netpay || selectedMetodo === PAYMENT_METHODS.NETPAY) {
                      setData([]);
                    }
                    if (metodo === PAYMENT_METHODS.NETPAY) {
                      setData([{
                        metodo,
                        key: 1,
                        importe: total,
                      }]);
                      form.setFieldsValue({ importe: total });
                    }
                  }}
                  labelProp="concepto"
                  showArrow
                  allowClear={false}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="importe"
                label="Importe"
                normalize={(val) => decimalNormalizer(val)}
              >
                <Input
                  className="input-importe"
                  disabled={solo_pago_netpay || !corte || PAYMENT_METHODS.NETPAY === _metodo}
                  ref={inputImporteRef}
                  onChange={onChangeImporte}
                />
              </Form.Item>
            </Col>

            {(showInput) && (
            <>
              <Col span={24}>
                <Form.Item
                  name="numero_de_cuenta"
                  rules={rules.required}
                  label={label}
                  autoComplete="off"
                  normalize={(val) => val.replace(INTEGER_PATTERN_REPLACE, '')}
                >
                  <Input
                    maxLength={selectedMetodo === 19 ? 18 : 4}
                    minLength={4}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="banco"
                  rules={rules.required}
                  label="Banco"
                  hidden={!requiereDatosBancarios}
                >
                  <Select dataSource={bancos} labelProp="nombre" />
                </Form.Item>
              </Col>
            </>
            )}
            {selectedMetodo === PAYMENT_METHODS.TRANSFERENCIA && (
            <Col span={24}>
              <NumericInput
                name="clabe_interbancaria"
                hidden={!requiereDatosBancarios}
                required
                label="Clabe Interbancaria"
                autoComplete="off"
              />
            </Col>
            )}
            {shouldShowField(selectedMetodo, 'METHODS_REQUIRING_DATE') && (
            <Col span={24}>
              <Form.Item
                name="fecha_de_autorizacion"
                rules={rules.required}
                label={getFieldLabel(selectedMetodo, 'DATE_LABELS')}
                hidden={!requiereDatosBancarios}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  placeholder=""
                />
              </Form.Item>
            </Col>
            )}
            {(showInput) && (
            <Col span={24}>
              <Form.Item
                name="numero_de_transaccion"
                required
                label="No. de Transacción"
                autoComplete="off"
                normalize={(val) => val.replace(INTEGER_PATTERN_REPLACE, '')}
              >
                <Input />
              </Form.Item>
            </Col>
            )}
            {shouldShowField(selectedMetodo, 'METHODS_REQUIRING_SUCURSAL') && (
            <Col span={24}>
              <Form.Item
                name="sucursal"
                rules={rules.required}
                label="Sucursal"
                hidden={!requiereDatosBancarios}
              >
                <Input
                  allowClear
                  autoComplete="off"
                />
              </Form.Item>
            </Col>
            )}
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
          {!(data[0]?.metodo === PAYMENT_METHODS.NETPAY || solo_pago_netpay) && (
          <Form className="without-feedback" layout="vertical">
            {data.map((e) => (
              <Col span={24} key={e.key}>
                <Row gutter={10} justify="space-around">
                  <Col flex="80%">
                    <Form.Item>
                      <Input
                        disabled
                        value={metodosDePago.find((item) => item.id === e.metodo)?.concepto}
                      />
                    </Form.Item>
                  </Col>
                  <Col flex="10%">
                    <Button
                      icon={<EditOutlined />}
                      onClick={() => {
                        const record = data.find((item) => item.key === e.key);
                        form.setFieldsValue({ ...formatReceived(record) });
                        setEditedPago(e);
                        setData(data.filter((item) => item.key !== e.key));
                        setSelectedMetodo(e.metodo);
                        focusInputImporte();
                      }}
                    />
                  </Col>
                  <Col flex="80%">
                    <Form.Item>
                      <Input disabled value={currencyFormatter(e.importe)} />
                    </Form.Item>
                  </Col>
                  <Col flex="10%">
                    <Button
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        const clone = data.filter((item) => item.key !== e.key);
                        const normalized = clone
                          .map((item, key) => ({ ...item, key }));
                        setData(normalized);
                        form.setFieldsValue({
                          metodo: !normalized
                            .some((item) => item.metodo === PAYMENT_METHODS.EFECTIVO)
                            ? PAYMENT_METHODS.EFECTIVO
                            : PAYMENT_METHODS.T_DEBITO,
                          importe: getFaltante(normalized),
                        });
                        setSelectedMetodo(normalized
                          .find((item) => item.metodo === PAYMENT_METHODS.EFECTIVO)?.metodo
                          || PAYMENT_METHODS.T_DEBITO);
                      }}
                    />
                  </Col>
                </Row>
              </Col>
            ))}
          </Form>
          )}
        </Col>
      </Row>
    </Spin>
  );
};

FormDatosDePago.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  setData: PropTypes.func.isRequired,
  onPayment: PropTypes.func.isRequired,
  setNetPayLoading: PropTypes.func.isRequired,
  total: PropTypes.number.isRequired,
  callback: PropTypes.func,
  autoFillImporte: PropTypes.bool,
  showImports: PropTypes.bool,
  allowAnyImporte: PropTypes.bool,
  setVisibleGetRecibos: PropTypes.func.isRequired,
  isConvenio: PropTypes.bool,
  onNetPayConvenio: PropTypes.func,
  padron: PropTypes.shape({
    id: PropTypes.number,
    tipo_de_padron: PropTypes.number,
    cargos: PropTypes.arrayOf(PropTypes.shape()),
    selectedRowKeys: PropTypes.arrayOf(PropTypes.number),
  }),
  parcialidades: PropTypes.arrayOf(PropTypes.number),
  convenio: PropTypes.number,
};

FormDatosDePago.defaultProps = {
  callback: () => {},
  autoFillImporte: true,
  showImports: true,
  allowAnyImporte: false,
  isConvenio: false,
  onNetPayConvenio: null,
  padron: null,
  parcialidades: [],
  convenio: null,
};

export default FormDatosDePago;
